import { createSelector } from 'reselect';
import { path, pathOr } from 'ramda';
import * as routeDetailModel from '../../models/routeDetail';
import * as parcelModel from '../../models/parcel';
import { getDriverRoutes, getRoutesList } from './routes';
import { isDeferredRequest } from '../../features/routeDetail/model';
import { routePlannerAddParcelRequests } from '../../models/request';

export const getRoute = (state) => state.route;

export const isCurrentUserLock = createSelector(
  getRoute,
  ({ currentUserLock }) => currentUserLock
);

export const isScheduleRequired = createSelector(
  getRoute,
  ({ scheduleRequired }) => scheduleRequired
);

export const getRequestOrder = createSelector(
  getRoute,
  ({ requestOrder }) => requestOrder
);

export const isSaveRouteEnabled = createSelector(
  getRoute,
  ({ saveRouteEnable }) => saveRouteEnable
);

export const getInitialDowngradeAttempts = createSelector(
  getRoute,
  ({ initialDowngradeAttempts }) => initialDowngradeAttempts
);

export const getRouteDetail = createSelector(getRoute, ({ routeDetail }) =>
  routeDetailModel.sortRouteStops(routeDetail)
);

export const getRouteStops = createSelector(
  getRouteDetail,
  (route) => route?.stop || []
);

export const getRouteDetailFetchError = createSelector(
  getRoute,
  ({ fetchError }) => fetchError
);

export const getDeliveryRouteStops = createSelector(
  getRouteStops,
  routeDetailModel.getDeliveryRouteStops
);

export const getRouteTransferredStops = createSelector(
  getRouteDetail,
  ({ transferredStop = [] }) => transferredStop
);

export const isRouteReadyOrOpen = createSelector(
  getRouteDetail,
  (routeDetail) =>
    routeDetailModel.isRouteStatusReady(routeDetail) ||
    routeDetailModel.isRouteStatusOpen(routeDetail)
);

export const isRouteStatusSuitableForResync = createSelector(
  getRouteDetail,
  (routeDetail) =>
    routeDetailModel.isRouteStatusReady(routeDetail) ||
    routeDetailModel.isRouteStatusOpen(routeDetail) ||
    routeDetailModel.isRouteStatusVoid(routeDetail)
);

export const isRouteStatusSuitableForReConsolidation = createSelector(
  getRouteDetail,
  (routeDetail) =>
    routeDetailModel.isRouteStatusReady(routeDetail) ||
    routeDetailModel.isRouteStatusOpen(routeDetail)
);

export const isRouteStatusLoading = createSelector(
  getRouteDetail,
  routeDetailModel.isRouteStatusLoading
);

export const isRouteStatusDeparted = createSelector(
  getRouteDetail,
  routeDetailModel.isRouteStatusDeparted
);

export const isRouteStatusInBuild = createSelector(
  getRouteDetail,
  routeDetailModel.isRouteStatusInBuild
);

export const getCurrentRouteId = createSelector(
  getRoute,
  ({ routeDetail }) => routeDetail?.routeId
);

export const getCurrentRouteDate = createSelector(
  getRoute,
  ({ routeDetail }) => routeDetail?.routeDate
);

export const isDefaultRoute = createSelector(
  getCurrentRouteId,
  getRoutesList,
  routeDetailModel.isDefaultRoute
);

export const getOriginalRouteDetail = createSelector(
  getRoute,
  ({ originalRouteDetail }) => originalRouteDetail
);

export const isCurrentRouteHasRequestWithoutResponse = createSelector(
  getRouteDetail,
  (routeDetail) =>
    pathOr([], ['request'], routeDetail).some(
      (item) =>
        !item.response && item.routeId === path(['routeId'], routeDetail)
    )
);

export const hasRouteUnprocessedRequest = createSelector(
  getRouteDetail,
  (routeDetail) =>
    pathOr([], ['request'], routeDetail).some(
      (request) => !request.response || isDeferredRequest(request)
    )
);

export const getRouteRequests = createSelector(
  getRouteDetail,
  (route) => route?.request || []
);

export const getUnprocessedRequest = createSelector(
  getRouteRequests,
  (requests) =>
    requests.filter(
      (request) => !request.response || isDeferredRequest(request)
    )
);

export const getRoutePlannerAddParcelRequests = createSelector(
  getRouteRequests,
  routePlannerAddParcelRequests
);

export const formatRouteDetailData = createSelector(
  getRouteDetail,
  isScheduleRequired,
  getInitialDowngradeAttempts,
  routeDetailModel.formatRouteDetailData
);

export const getTransferRouteId = createSelector(
  getRoute,
  ({ transferRouteId }) => transferRouteId
);

export const isTransferRouteDefault = createSelector(
  getTransferRouteId,
  getRoutesList,
  routeDetailModel.isDefaultRoute
);

export const getTransferRouteCode = createSelector(
  getTransferRouteId,
  getRoutesList,
  (routeId, routes) => routes[routeId]?.routeCode || routeId
);

export const getRouteLockedBy = createSelector(
  getDriverRoutes,
  getRouteDetail,
  (routes, route) => routeDetailModel.getRouteLockedBy(routes, route?.routeId)
);

export const getOriginalParcelsTransferData = createSelector(
  getOriginalRouteDetail,
  parcelModel.getOriginalParcelsTransferData
);

export const getDriverLocations = createSelector(
  getRouteDetail,
  (route) => route?.driverLocations
);
